// BOORO: the folders of the knowledge and of the assets. Pages and files of a
// place lie in the same folders; a page, a file or a folder moves by dragging
// it onto another folder, and files from the computer land where they are
// dropped. The assets show only the folders here and open one to see its files.

import { folderAncestors, folderName, inFolder } from "@booro/knowledge";
import { Trans, useLingui } from "@lingui/react/macro";
import type {
  AccessSubject,
  KnowledgeFileSummary,
  KnowledgeFolder,
  KnowledgePageSummary,
} from "@rakazo/contracts";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
} from "@rakazo/ui-web";
import type { LucideIcon } from "lucide-react";
import {
  Building,
  ChevronRight,
  File,
  FileImage,
  FileText,
  Folder,
  FolderInput,
  FolderPlus,
  Globe,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import type { DragEvent, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

export type KnowledgeItem = { kind: "page" | "file"; id: string };

/** A place pages lie in; one without a name is the only one and needs no head. */
type Place = { id: string | null; name: string };

/** What is being dragged inside the tree. Files from the computer are not here. */
type Dragged =
  | { kind: "page" | "file"; id: string; spaceId: string | null }
  | { kind: "folder"; path: string; spaceId: string | null };

const placeKey = (spaceId: string | null) => spaceId ?? "account";
const folderKey = (spaceId: string | null, path: string) => `${placeKey(spaceId)}:${path}`;
const parentOf = (path: string) => (path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "");

export function fileIcon(mimeType: string): LucideIcon {
  if (mimeType.startsWith("image/")) return FileImage;
  if (mimeType.startsWith("text/") || mimeType === "application/pdf") return FileText;
  return File;
}

export function KnowledgeTree({
  places,
  pages,
  files,
  folders,
  query,
  selected,
  reachIcon,
  onSelect,
  onCreatePage,
  onCreateFolder,
  onRenameFolder,
  onRemoveFolder,
  onUpload,
  onMove,
  openFolder,
  onOpenFolder,
}: {
  places: Place[];
  pages: KnowledgePageSummary[];
  files: KnowledgeFileSummary[];
  folders: KnowledgeFolder[];
  query: string;
  selected: KnowledgeItem | null;
  reachIcon: (receivers: readonly AccessSubject[]) => ReactNode;
  onSelect: (item: KnowledgeItem) => void;
  /** Left out where there are no pages, as in the assets. */
  onCreatePage?: (spaceId: string | null, folder: string) => void;
  onCreateFolder: (spaceId: string | null, path: string) => Promise<void>;
  onRenameFolder: (spaceId: string | null, from: string, to: string) => Promise<void>;
  onRemoveFolder: (spaceId: string | null, path: string) => void;
  onUpload: (spaceId: string | null, folder: string, files: File[]) => void;
  onMove: (item: KnowledgeItem, folder: string) => void;
  /** The folder that is open beside the tree, when a click opens folders. */
  openFolder?: { spaceId: string | null; path: string } | null;
  onOpenFolder?: (spaceId: string | null, path: string) => void;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // A folder being named: a new one under a parent, or an existing one.
  const [naming, setNaming] = useState<
    | { mode: "new"; spaceId: string | null; parent: string }
    | { mode: "rename"; spaceId: string | null; path: string }
    | null
  >(null);
  const dragged = useRef<Dragged | null>(null);
  const uploadTarget = useRef<{ spaceId: string | null; folder: string } | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // The folders that hold the open item stay open.
  useEffect(() => {
    if (!selected) return;
    const item =
      selected.kind === "page"
        ? pages.find((entry) => entry.id === selected.id)
        : files.find((entry) => entry.id === selected.id);
    if (!item?.folder) return;
    setOpen((current) => {
      const next = new Set(current);
      for (const path of folderAncestors(item.folder)) next.add(folderKey(item.spaceId, path));
      return next.size === current.size ? current : next;
    });
  }, [selected, pages, files]);

  const foldersByPlace = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const folder of folders) {
      const key = placeKey(folder.spaceId);
      map.set(key, [...(map.get(key) ?? []), folder.path]);
    }
    return map;
  }, [folders]);

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const isOpenFolder = (spaceId: string | null, path: string) =>
    openFolder?.spaceId === spaceId && openFolder.path === path;

  /** A click on a folder: folds it, or, where folders open beside the tree, opens it. */
  function clickFolder(spaceId: string | null, path: string) {
    const key = folderKey(spaceId, path);
    if (!onOpenFolder || isOpenFolder(spaceId, path)) {
      toggle(key);
      return;
    }
    setOpen((current) => new Set(current).add(key));
    onOpenFolder(spaceId, path);
  }

  function chooseFiles(spaceId: string | null, folder: string) {
    uploadTarget.current = { spaceId, folder };
    fileInput.current?.click();
  }

  /** What a folder or a place does when something is dropped on it. */
  function dropZone(spaceId: string | null, folder: string) {
    const key = folderKey(spaceId, folder);
    const accepts = (event: DragEvent) => {
      if (event.dataTransfer.types.includes("Files")) return true;
      const item = dragged.current;
      if (!item || item.spaceId !== spaceId) return false;
      // A folder cannot go into itself or stay where it is.
      if (item.kind === "folder")
        return !inFolder(folder, item.path) && parentOf(item.path) !== folder;
      return true;
    };
    return {
      onDragOver: (event: DragEvent) => {
        if (!accepts(event)) return;
        event.preventDefault();
        event.stopPropagation();
        setDropTarget(key);
      },
      onDragLeave: (event: DragEvent) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDropTarget((current) => (current === key ? null : current));
        }
      },
      onDrop: (event: DragEvent) => {
        if (!accepts(event)) return;
        event.preventDefault();
        event.stopPropagation();
        setDropTarget(null);
        const dropped = [...event.dataTransfer.files];
        if (dropped.length > 0) {
          onUpload(spaceId, folder, dropped);
          return;
        }
        const item = dragged.current;
        dragged.current = null;
        if (!item) return;
        if (item.kind === "folder") {
          const name = folderName(item.path);
          void onRenameFolder(spaceId, item.path, folder ? `${folder}/${name}` : name);
        } else {
          onMove({ kind: item.kind, id: item.id }, folder);
        }
        if (folder) setOpen((current) => new Set(current).add(key));
      },
    };
  }

  const dragStart = (item: Dragged) => (event: DragEvent) => {
    dragged.current = item;
    event.dataTransfer.effectAllowed = "move";
    // Firefox only starts a drag that carries data.
    event.dataTransfer.setData("text/plain", item.kind === "folder" ? item.path : item.id);
  };

  const needle = query.trim().toLowerCase();

  function itemRows(spaceId: string | null, folder: string, depth: number) {
    const placePages = pages.filter(
      (entry) => entry.spaceId === spaceId && entry.folder === folder,
    );
    const placeFiles = files.filter(
      (entry) => entry.spaceId === spaceId && entry.folder === folder,
    );
    return (
      <>
        {placePages.map((entry) => (
          <ItemRow
            key={entry.id}
            testId={`knowledge-page-${entry.id}`}
            depth={depth}
            icon={FileText}
            label={entry.title}
            active={selected?.kind === "page" && selected.id === entry.id}
            reach={reachIcon(entry.receivers)}
            onClick={() => onSelect({ kind: "page", id: entry.id })}
            onDragStart={dragStart({ kind: "page", id: entry.id, spaceId })}
          />
        ))}
        {placeFiles.map((entry) => (
          <ItemRow
            key={entry.id}
            testId={`knowledge-file-${entry.id}`}
            depth={depth}
            icon={fileIcon(entry.mimeType)}
            label={entry.name}
            active={selected?.kind === "file" && selected.id === entry.id}
            reach={reachIcon(entry.receivers)}
            onClick={() => onSelect({ kind: "file", id: entry.id })}
            onDragStart={dragStart({ kind: "file", id: entry.id, spaceId })}
          />
        ))}
      </>
    );
  }

  function folderRows(spaceId: string | null, parent: string, depth: number): ReactNode {
    const children = (foldersByPlace.get(placeKey(spaceId)) ?? []).filter(
      (path) => parentOf(path) === parent,
    );
    return (
      <>
        {naming?.mode === "new" && naming.spaceId === spaceId && naming.parent === parent ? (
          <FolderNameInput
            depth={depth}
            initial=""
            onDone={async (name) => {
              setNaming(null);
              if (name) await onCreateFolder(spaceId, parent ? `${parent}/${name}` : name);
            }}
          />
        ) : null}
        {children.map((path) => {
          const key = folderKey(spaceId, path);
          const expanded = open.has(key);
          if (naming?.mode === "rename" && naming.spaceId === spaceId && naming.path === path) {
            return (
              <FolderNameInput
                key={key}
                depth={depth}
                initial={folderName(path)}
                onDone={async (name) => {
                  setNaming(null);
                  if (name && name !== folderName(path)) {
                    await onRenameFolder(spaceId, path, parent ? `${parent}/${name}` : name);
                  }
                }}
              />
            );
          }
          return (
            <li key={key}>
              {/* biome-ignore lint/a11y/noStaticElementInteractions: dragging is a shortcut; pages and files also move through their move menu. */}
              <div
                {...dropZone(spaceId, path)}
                draggable
                onDragStart={dragStart({ kind: "folder", path, spaceId })}
                data-testid={`knowledge-folder-${path}`}
                className={`group flex items-center rounded-lg ${
                  dropTarget === key
                    ? "bg-accent ring-1 ring-ring"
                    : isOpenFolder(spaceId, path)
                      ? "bg-accent font-medium"
                      : "hover:bg-accent"
                }`}
              >
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-current={isOpenFolder(spaceId, path) ? "true" : undefined}
                  onClick={() => clickFolder(spaceId, path)}
                  className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pe-1 text-start text-[13.5px]"
                  style={{ paddingInlineStart: 8 + depth * 14 }}
                >
                  <ChevronRight
                    size={13}
                    aria-hidden="true"
                    className={`shrink-0 text-muted-foreground transition-transform rtl:-scale-x-100 ${
                      expanded ? "rotate-90 rtl:-rotate-90" : ""
                    }`}
                  />
                  <Folder size={14} aria-hidden="true" className="shrink-0 text-muted-foreground" />
                  <span className="truncate" dir="auto">
                    {folderName(path)}
                  </span>
                </button>
                <FolderMenu
                  label={t`Folder actions`}
                  onCreatePage={onCreatePage ? () => onCreatePage(spaceId, path) : undefined}
                  onCreateFolder={() => {
                    setOpen((current) => new Set(current).add(key));
                    setNaming({ mode: "new", spaceId, parent: path });
                  }}
                  onUpload={() => chooseFiles(spaceId, path)}
                  onRename={() => setNaming({ mode: "rename", spaceId, path })}
                  onRemove={() => onRemoveFolder(spaceId, path)}
                />
              </div>
              {expanded ? (
                <ul className="space-y-0.5">
                  {folderRows(spaceId, path, depth + 1)}
                  {itemRows(spaceId, path, depth + 1)}
                </ul>
              ) : null}
            </li>
          );
        })}
      </>
    );
  }

  const matches = needle
    ? {
        pages: pages.filter(
          (entry) =>
            entry.title.toLowerCase().includes(needle) || entry.path.toLowerCase().includes(needle),
        ),
        files: files.filter((entry) => entry.name.toLowerCase().includes(needle)),
      }
    : null;

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        data-testid="knowledge-upload-input"
        onChange={(event) => {
          const target = uploadTarget.current;
          const chosen = [...(event.target.files ?? [])];
          event.target.value = "";
          if (target && chosen.length > 0) onUpload(target.spaceId, target.folder, chosen);
        }}
      />
      {places.map((place) => {
        const key = folderKey(place.id, "");
        if (matches) {
          const placePages = matches.pages.filter((entry) => entry.spaceId === place.id);
          const placeFiles = matches.files.filter((entry) => entry.spaceId === place.id);
          if (placePages.length + placeFiles.length === 0) return null;
          return (
            <div key={key} className="mb-3">
              <PlaceHeader place={place} />
              <ul className="space-y-0.5">
                {placePages.map((entry) => (
                  <ItemRow
                    key={entry.id}
                    testId={`knowledge-page-${entry.id}`}
                    depth={0}
                    icon={FileText}
                    label={entry.title}
                    detail={entry.folder}
                    active={selected?.kind === "page" && selected.id === entry.id}
                    reach={reachIcon(entry.receivers)}
                    onClick={() => onSelect({ kind: "page", id: entry.id })}
                  />
                ))}
                {placeFiles.map((entry) => (
                  <ItemRow
                    key={entry.id}
                    testId={`knowledge-file-${entry.id}`}
                    depth={0}
                    icon={fileIcon(entry.mimeType)}
                    label={entry.name}
                    detail={entry.folder}
                    active={selected?.kind === "file" && selected.id === entry.id}
                    reach={reachIcon(entry.receivers)}
                    onClick={() => onSelect({ kind: "file", id: entry.id })}
                  />
                ))}
              </ul>
            </div>
          );
        }
        return (
          <div key={key} className="mb-3">
            <div
              {...dropZone(place.id, "")}
              className={`group flex items-center rounded-lg ${
                dropTarget === key ? "bg-accent ring-1 ring-ring" : ""
              }`}
            >
              {onOpenFolder ? (
                <button
                  type="button"
                  aria-current={isOpenFolder(place.id, "") ? "true" : undefined}
                  onClick={() => onOpenFolder(place.id, "")}
                  className={`flex min-w-0 flex-1 rounded-lg text-start ${
                    isOpenFolder(place.id, "") ? "bg-accent" : "hover:bg-accent"
                  }`}
                >
                  <PlaceHeader place={place} />
                </button>
              ) : (
                <PlaceHeader place={place} />
              )}
              <FolderMenu
                label={place.name ? t`Add to ${place.name}` : t`Add`}
                onCreatePage={onCreatePage ? () => onCreatePage(place.id, "") : undefined}
                onCreateFolder={() => setNaming({ mode: "new", spaceId: place.id, parent: "" })}
                onUpload={() => chooseFiles(place.id, "")}
              />
            </div>
            <ul className="space-y-0.5">
              {folderRows(place.id, "", 0)}
              {itemRows(place.id, "", 0)}
            </ul>
          </div>
        );
      })}
      {matches && matches.pages.length + matches.files.length === 0 ? (
        <p className="px-2 py-1 text-[13px] text-muted-foreground">
          <Trans>Nothing found</Trans>
        </p>
      ) : null}
    </>
  );
}

function PlaceHeader({ place }: { place: Place }) {
  if (!place.name) return <div className="min-w-0 flex-1" />;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1 text-[12px] uppercase tracking-[0.08em] text-muted-foreground">
      {place.id === null ? (
        <Globe size={12} aria-hidden="true" />
      ) : (
        <Building size={12} aria-hidden="true" />
      )}
      <span className="truncate" dir="auto">
        {place.name}
      </span>
    </div>
  );
}

function ItemRow({
  testId,
  depth,
  icon: Icon,
  label,
  detail,
  active,
  reach,
  onClick,
  onDragStart,
}: {
  testId: string;
  depth: number;
  icon: LucideIcon;
  label: string;
  detail?: string;
  active: boolean;
  reach: ReactNode;
  onClick: () => void;
  onDragStart?: (event: DragEvent) => void;
}) {
  return (
    <li>
      <button
        type="button"
        data-testid={testId}
        draggable={Boolean(onDragStart)}
        onDragStart={onDragStart}
        onClick={onClick}
        className={`flex w-full items-center gap-1.5 rounded-lg py-1.5 pe-2 text-start text-[13.5px] hover:bg-accent ${
          active ? "bg-accent font-medium" : ""
        }`}
        // Items line up with the name of their folder, past its chevron.
        style={{ paddingInlineStart: 8 + depth * 14 + 19 }}
      >
        <Icon size={14} aria-hidden="true" className="shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">
          <span className="block truncate" dir="auto">
            {label}
          </span>
          {detail ? (
            <span className="block truncate text-[11.5px] text-muted-foreground" dir="auto">
              {detail}
            </span>
          ) : null}
        </span>
        {reach}
      </button>
    </li>
  );
}

function FolderMenu({
  label,
  onCreatePage,
  onCreateFolder,
  onUpload,
  onRename,
  onRemove,
}: {
  label: string;
  onCreatePage?: () => void;
  onCreateFolder: () => void;
  onUpload: () => void;
  onRename?: () => void;
  onRemove?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={label}
            className="me-1 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[popup-open]:opacity-100"
          />
        }
      >
        {onRename ? <MoreHorizontal /> : <Plus />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onCreatePage ? (
          <DropdownMenuItem onClick={onCreatePage}>
            <FileText />
            <Trans>New page</Trans>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onClick={onCreateFolder}>
          <FolderPlus />
          <Trans>New folder</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onUpload}>
          <Upload />
          <Trans>Upload files</Trans>
        </DropdownMenuItem>
        {onRename || onRemove ? <DropdownMenuSeparator /> : null}
        {onRename ? (
          <DropdownMenuItem onClick={onRename}>
            <Pencil />
            <Trans>Rename</Trans>
          </DropdownMenuItem>
        ) : null}
        {onRemove ? (
          <DropdownMenuItem variant="destructive" onClick={onRemove}>
            <Trash2 />
            <Trans>Delete folder</Trans>
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FolderNameInput({
  depth,
  initial,
  onDone,
}: {
  depth: number;
  initial: string;
  onDone: (name: string) => Promise<void> | void;
}) {
  const { t } = useLingui();
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const finish = (name: string) => {
    if (done.current) return;
    done.current = true;
    void onDone(name.trim().replaceAll("/", "-"));
  };
  return (
    <li className="flex items-center gap-1.5 py-0.5" style={{ paddingInlineStart: 8 + depth * 14 }}>
      <Folder size={14} aria-hidden="true" className="ms-[19px] shrink-0 text-muted-foreground" />
      <Input
        autoFocus
        value={value}
        aria-label={t`Folder name`}
        placeholder={t`Folder name`}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => finish(value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") finish(value);
          if (event.key === "Escape") finish("");
        }}
        className="h-7 text-[13px]"
      />
    </li>
  );
}

/** Moves a page or a file into another folder of its place, without dragging. */
export function MoveMenu({
  folders,
  current,
  onMove,
}: {
  /** The folders of the place the item lies in. */
  folders: readonly string[];
  current: string;
  onMove: (folder: string) => void;
}) {
  const { t } = useLingui();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button size="icon-sm" variant="ghost" aria-label={t`Move to folder`} />}
      >
        <FolderInput />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[min(420px,calc(100vh-16px))] overflow-y-auto"
      >
        <DropdownMenuItem disabled={current === ""} onClick={() => onMove("")}>
          <Folder />
          <Trans>Top level</Trans>
        </DropdownMenuItem>
        {folders.map((path) => (
          <DropdownMenuItem key={path} disabled={path === current} onClick={() => onMove(path)}>
            <Folder />
            <span dir="auto">{path}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
